import {useEffect, useMemo, useRef, useState} from 'react';

import styled from 'styled-components';
import {every, reduce} from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';

import {attachModal} from '~/shared/components/ReduxModal';
import {fetchDiscountInfoById} from '~/shared/services/bffService/api';
import {getLocalizationService} from '~/shared/services/localisationService';
import {OrderData} from '~/shared/store/models';
import {body14Normal, body14Bold, body18Bold, body12Normal} from '~/shared/theme/typography';
import {IDiscountResponse} from '~/shared/services/bffService/api/interfaces/responses';
import {media} from '~/shared/theme/media';
import {createLogger} from '~/shared/logging';
import {handleRefreshToken} from '~/shared/services/auth';
import {SkeletonLoader, Skeleton} from '~/shared/components/Loaders';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import useWebpWithFallback from '~/shared/hooks/useWebpWithFallback';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {is401Error} from '~/shared/services/apiErrorService';
import {isJustEat} from '~/shared/config';
import tenbisBagImg from '~/assets/images/discounts/tenbis-bag.png';
import justeatBagImg from '~/assets/images/discounts/justeat-bag.png';
import {PieCloseModalButton} from '~/shared/components/Modals/styled';
import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';

const logger = createLogger('Discounts');

const Root = styled.div`
  && {
    ${flexColumn}
    width: 100%;
    background-color: ${({theme}) => theme.colors.surface};
    ${media.minLargeMobile`
      width: 500px;
      border-radius: 4px;
    `}
  }
`;

const DiscountDetailsWrapper = styled.div`
  color: ${({theme}) => theme.colors.contentDefault};
  padding: 24px 16px 16px;
  ${media.minLargeMobile`
    padding: 24px;
  `}
`;

const Title = styled.div`
  ${body18Bold}
  line-height: 26px;
  margin-bottom: 8px;
`;

const CouponDetails = styled.div`
  ${body14Normal}
  line-height: 20px;
`;

const Section = styled.div`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({theme}) => theme.colors.blackOpacity08};
`;

const SectionTitle = styled.div`
  ${body18Bold}
  margin-bottom: 8px;
  line-height: 26px;
`;
const SectionDescription = styled.div`
  ${body14Normal}
  line-height: 20px;
`;

const BoldText = styled.span`
  ${body14Bold}
  display: inline-block;
  width: ${({theme}) => (theme.isLTR ? '38px' : '21px')};
`;

const DisclaimerText = styled.div`
  ${body12Normal}
  line-height: 16px;
  color: ${({theme}) => theme.colors.contentInteractiveSubdued};
`;

const Img = styled(ImageWithAlt)`
  object-fit: cover;
  height: 132px;
  ${media.minLargeMobile`
    height: 152px;
  `}
`;

const HeaderImageSection = styled.div`
  height: 170px;
  overflow: hidden;
  ${media.minLargeMobile`
    height: 192px;
  `}
`;

const BagImgContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

const BagImg = styled(ImageWithAlt)`
  object-fit: contain;
  height: 136px;
  width: 136px;
  ${media.minLargeMobile`
    height: 160px;
    width: 160px;
  `}
`;

const StyledPieCloseModalButton = styled(PieCloseModalButton)`
  background-color: ${({theme}) => theme.colors.surface};
  border: 1px solid ${({theme}) => theme.colors.borderStrong};
`;

const FULL_DAY_START_TIME = '00:00';
const FULL_DAY_END_TIME = '23:59';

const ALWAYS_APPLICABLE_DAYS_NUMBER = 7;
const WEEK_DAYS_ONLY_DAYS_NUMBER = 5;

interface IDiscountModal {
  closeModal: () => void;
  args: {
    discountId: OrderData['discountCoupon']['id'];
  };
}

const DiscountLoader = () => {
  const {t} = getLocalizationService();

  return (
    <DiscountDetailsWrapper>
      <Title id="modal-title">{t('offer_details')}</Title>
      <Section>
        <Skeleton height={24} />
      </Section>

      <Section>
        <SectionTitle>{t('offer_hours')}</SectionTitle>
        <Skeleton height={20} />
      </Section>
      <Section>
        <SectionTitle>{t('valid_until_2')}</SectionTitle>
        <Skeleton height={20} />
      </Section>
      <Section>
        <Skeleton height={20} />
        <Skeleton height={20} />
        <Skeleton height={20} />
      </Section>
    </DiscountDetailsWrapper>
  );
};

const DiscountInfoModal = ({args, closeModal}: IDiscountModal) => {
  const {t, currentLanguageKey} = getLocalizationService();
  const isMinLargeMobile = useIsMinLargeMobile();
  const {discountId} = args;
  const isRtl = currentLanguageKey === 'he';
  const discountInfoRequested = useRef(false);

  const [discountInfo, setdiscountInfo] = useState<IDiscountResponse | null>(null);

  const discountImg = useWebpWithFallback('discounts/discount-header-background.jpg') as string;

  const discountOperatingHours = useMemo(() => {
    if (!discountInfo?.activityTimes) {
      return null;
    }
    const discountFullDays = reduce(
      discountInfo?.activityTimes,
      (count, day) => {
        const isFullDay = every(
          day.ranges,
          range => range.startTime === FULL_DAY_START_TIME && range.endTime === FULL_DAY_END_TIME,
        );
        return isFullDay ? count + 1 : count;
      },
      0,
    );

    const isWeekdayOnly =
      discountFullDays === WEEK_DAYS_ONLY_DAYS_NUMBER &&
      discountFullDays === discountInfo.activityTimes.length &&
      discountInfo.activityTimes.map(day => day.dayOfWeek).every((day, index) => day === index + 1); // [1, 2, 3, 4, 5] match sunday - thursday

    if (isWeekdayOnly) {
      return t('valid_on_weekdays_only');
    }

    if (discountFullDays === ALWAYS_APPLICABLE_DAYS_NUMBER) {
      return t('always_applicable');
    }

    return discountInfo?.activityTimes?.map(({dayOfWeek, ranges}) => {
      const dayOfWeekLabel = moment()
        .locale(currentLanguageKey)
        .day(dayOfWeek - 1)
        .format('ddd');

      const rangesText = ranges.sort((a, b) => (isRtl ? b.startTime.localeCompare(a.startTime) : a.startTime.localeCompare(b.startTime))).map(range => (isRtl ? `${range.endTime} - ${range.startTime}` : `${range.startTime} - ${range.endTime}`)).join(', ');

      return (
        <SectionDescription>
          <BoldText>
            {dayOfWeekLabel}
          </BoldText>
          {rangesText}
        </SectionDescription>
      );
    });
  }, [currentLanguageKey, discountInfo, t, isRtl]);

  useEffect(() => {
    if (discountInfoRequested.current && !discountId) {
      return;
    }

    const fetchDiscountData = async () => {
      try {
        const discountInfoResponse = await fetchDiscountInfoById({discountId});
        setdiscountInfo(discountInfoResponse);
      } catch (error) {
        if (is401Error(error)) {
          await handleRefreshToken(error, fetchDiscountData);
          return;
        }
        logger.error('failed to fetch discount by id', error);
      }
    };
    fetchDiscountData();

    discountInfoRequested.current = true;
  }, [discountId]);

  return (
    <Root>
      {isMinLargeMobile && <StyledPieCloseModalButton onClick={closeModal} aria-label={t('close')}/>}
      <HeaderImageSection>
        <Img src={discountImg} noAlt />
        <BagImgContainer>
          <BagImg src={isJustEat ? justeatBagImg : tenbisBagImg} noAlt />
        </BagImgContainer>
      </HeaderImageSection>
      <SkeletonLoader shouldShowLoader={!discountInfo} LoaderComponent={DiscountLoader}>
        <DiscountDetailsWrapper>
          <Title id="modal-title">{t('offer_details')}</Title>
          <Section>
            <CouponDetails>{discountInfo?.details}</CouponDetails>
          </Section>
          <Section>
            <SectionTitle>{t('offer_hours')}</SectionTitle>
            <SectionDescription>{discountOperatingHours}</SectionDescription>
          </Section>
          <Section>
            <SectionTitle>{t('valid_until_2')}</SectionTitle>
            <SectionDescription>{discountInfo?.expirationDate}</SectionDescription>
          </Section>
          <DisclaimerText>{t('discount_disclaimer_1')}</DisclaimerText>
          <DisclaimerText>{t('discount_disclaimer_2')}</DisclaimerText>
          <DisclaimerText>{t('discount_disclaimer_3')}</DisclaimerText>
        </DiscountDetailsWrapper>
      </SkeletonLoader>
    </Root>
  );
};

DiscountInfoModal.modalType = 'autoWidth';
DiscountInfoModal.mobileBottomSheet = true;
DiscountInfoModal.hideCloseButton = true;
DiscountInfoModal.absoluteBottomSheetBadge = true;

attachModal('discountInfoModal', DiscountInfoModal);
